import { createReducer, getType } from "typesafe-actions";

import ChatVoiceHint from "@mapmycustomers/shared/enum/chat/ChatVoiceHint";
import ChatVoicePhase from "@mapmycustomers/shared/enum/chat/ChatVoicePhase";

import { APP_CHAT_THREAD_KEY } from "@app/util/chat/getChatThreadKey";

import {
  cancelVoiceRecording,
  ChatVoiceActions,
  enterVoiceReview,
  finishVoiceRecording,
  pauseVoiceRecording,
  resumeVoiceRecording,
  sendVoiceMessage,
  setVoiceError,
  setVoiceHint,
  setVoicePart,
  startVoiceRecording,
} from "./actions";
import type ChatVoiceState from "./ChatVoiceState";

export const chatVoiceInitialState: ChatVoiceState = {
  elapsedMs: 0,
  hint: ChatVoiceHint.NONE,
  parts: {},
  phase: ChatVoicePhase.IDLE,
  threadKey: APP_CHAT_THREAD_KEY,
};

// Banking is what excludes paused time: a pause adds what ran and then contributes nothing until
// the mark is set again. Both pause and finish bank — finish especially, or a rep who never
// paused sends a message tagged 0:00.
const bank = (
  state: ChatVoiceState,
  at: number
): Pick<ChatVoiceState, "elapsedMs" | "segmentStartedAt"> => ({
  elapsedMs:
    state.segmentStartedAt === undefined
      ? state.elapsedMs
      : state.elapsedMs + (at - state.segmentStartedAt),
  segmentStartedAt: undefined,
});

const chatVoice = createReducer<ChatVoiceState, ChatVoiceActions>(chatVoiceInitialState)
  .handleType(getType(startVoiceRecording), (state, { payload }) => ({
    ...chatVoiceInitialState,
    phase: ChatVoicePhase.RECORDING,
    segmentStartedAt: payload.startedAt,
    threadKey: payload.threadKey,
  }))
  .handleType(getType(pauseVoiceRecording), (state, { payload }) => ({
    ...state,
    ...bank(state, payload.at),
    phase: ChatVoicePhase.PAUSED,
  }))
  .handleType(getType(resumeVoiceRecording), (state, { payload }) => ({
    ...state,
    phase: ChatVoicePhase.RECORDING,
    segmentStartedAt: payload.at,
  }))
  .handleType(getType(finishVoiceRecording), (state, { payload }) => ({
    ...state,
    ...bank(state, payload.at),
    hint: ChatVoiceHint.NONE,
    phase: ChatVoicePhase.TRANSCRIBING,
  }))
  .handleType(getType(setVoicePart), (state, { payload }) => ({
    ...state,
    parts: { ...state.parts, [payload.seq]: payload.text },
  }))
  .handleType(getType(setVoiceHint), (state, { payload }) => ({ ...state, hint: payload.hint }))
  .handleType(getType(setVoiceError), (state, { payload }) => ({ ...state, error: payload.error }))
  .handleType(getType(enterVoiceReview), (state) => ({
    ...state,
    hint: ChatVoiceHint.NONE,
    phase: ChatVoicePhase.REVIEW,
  }))
  .handleType(getType(cancelVoiceRecording), () => chatVoiceInitialState)
  .handleType(getType(sendVoiceMessage.request), () => chatVoiceInitialState);

export default chatVoice;
